import { Box, Button, Dialog, DialogContent, List, ListItem, Typography } from '@mui/material';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { fetchActiveQuiz, fetchQuizResult } from '../../actions/quiz';
import QuizTypes from '../../constants/quiz';
import usePhoneCheck from '../../hooks/usePhoneCheck';

export const QuizItem = () => {
    const dispatch = useDispatch();
    const { t } = useTranslation();
    const theme = localStorage.getItem('theme');
    const isMobile = usePhoneCheck();

    const [stepAnswers, setStepAnswers] = useState({});
    const [step, setStep] = useState(0);
    const [activeAnswers, setActiveAnswers] = useState({});
    const [imageDialogOpen, setImageDialogOpen] = useState(false);

    const { activeQuiz, selectedQuizId } = useSelector(state => state.quiz);

    useEffect(() => {
        dispatch(fetchActiveQuiz(selectedQuizId));

        return () => {
            setStepAnswers({});
            setActiveAnswers({});
            setStep(0);
            dispatch({
                type: QuizTypes.SET_ACTIVE_QUIZ,
                payload: {},
            });
        };
    }, []);

    useEffect(() => {
        if (!activeQuiz || !activeQuiz.questions || activeQuiz.questions.length === 0) {
            return;
        }

        const activeOption = activeQuiz.questions[step];
        const correctAnswerType = activeOption?.correct_answer_type ?? 'single';

        const savedAnswers = stepAnswers[step];
        if (savedAnswers) {
            setActiveAnswers(savedAnswers);
        } else {
            setActiveAnswers(correctAnswerType === 'multiple' ? [] : {});
        }
    }, [step, stepAnswers, activeQuiz]);

    const onClickNext = () => {
        setStepAnswers(prev => ({
            ...prev,
            [step]: activeAnswers,
        }));

        setStep(prev => prev + 1);
    };

    const onClickPrev = () => {
        setStepAnswers(prev => ({
            ...prev,
            [step]: activeAnswers,
        }));

        setStep(prev => prev - 1);
    };

    const onClickResult = async () => {
        const finalStepAnswers = {
            ...stepAnswers,
            [step]: activeAnswers,
        };

        const answersArray = Object.keys(finalStepAnswers)
            .sort((a, b) => parseInt(a) - parseInt(b))
            .map(stepKey => finalStepAnswers[stepKey]);

        dispatch(fetchQuizResult(answersArray, activeQuiz.id));

        setStepAnswers({});
        setActiveAnswers({});
        setStep(0);
        dispatch({
            type: QuizTypes.SET_SHOW_RESULT,
            payload: true,
        });
    };

    const getCurrentCorrectAnswerType = () => {
        if (!activeQuiz || !activeQuiz.questions || activeQuiz.questions.length === 0) {
            return 'single';
        }
        const activeOption = activeQuiz.questions[step];
        return activeOption?.correct_answer_type ?? 'single';
    };

    const handleAnswerClick = answer => {
        const correctAnswerType = getCurrentCorrectAnswerType();

        if (correctAnswerType === 'multiple') {
            setActiveAnswers(prev => {
                const currentAnswers = Array.isArray(prev) ? prev : [];
                const exists = currentAnswers.find(a => a.id === answer.id);
                return exists ? currentAnswers.filter(a => a.id !== answer.id) : [...currentAnswers, answer];
            });
        } else {
            setActiveAnswers(answer);
        }
    };

    const hasActiveAnswers = () => {
        const correctAnswerType = getCurrentCorrectAnswerType();

        if (correctAnswerType === 'multiple') {
            return Array.isArray(activeAnswers) && activeAnswers.length > 1;
        }
        return activeAnswers && Object.keys(activeAnswers).length > 0;
    };

    const isAnswerSelected = answer => {
        const correctAnswerType = getCurrentCorrectAnswerType();

        if (correctAnswerType === 'multiple') {
            return Array.isArray(activeAnswers) && activeAnswers.some(a => a.id === answer.id);
        }
        return activeAnswers && activeAnswers.id === answer.id;
    };

    const handleOpenDialogImage = () => {
        if (isMobile) {
            return;
        }
        setImageDialogOpen(true);
    };

    if (!activeQuiz || !activeQuiz.questions || activeQuiz.questions.length === 0) {
        return <p>Ошибка загрузки данных</p>;
    }

    const activeOption = activeQuiz.questions[step];

    return (
        <>
            <Box
                sx={{
                    width: isMobile ? '98%' : '900px',
                    position: 'absolute',
                    top: '50%',
                    left: '50%',
                    transform: 'translate(-50%, -50%)',
                    backgroundColor: theme === 'dark' ? '#464646' : '#fff',
                    p: isMobile ? 3 : 5,
                    borderRadius: 1,
                }}
            >
                <Box
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                    }}
                >
                    <Typography variant="h3" component="body1" color={theme === 'dark' ? '#fff' : 'inherit'}>
                        {t('quiz.theme')}: {activeQuiz.theme.toLowerCase()} №{activeQuiz.templates_id}
                    </Typography>
                    <Typography variant="span" component="body1" color={theme === 'dark' ? '#fff' : 'inherit'}>
                        {step + 1} / {activeQuiz.questions?.length}
                    </Typography>
                </Box>
                <Box>
                    <>
                        {activeOption.correct_answer_type === 'multiple' ? (
                            <Typography variant="body1" color={theme === 'dark' ? '#fff' : 'inherit'}>
                                {t('quiz.selectMultipleAnswers')} <br />
                            </Typography>
                        ) : null}
                        <Typography variant="h6" component="h6" color={theme === 'dark' ? '#fff' : 'inherit'}>
                            {activeOption.question_text}
                        </Typography>

                        {activeOption.imageFile && (
                            <Box
                                onClick={handleOpenDialogImage}
                                component="img"
                                sx={{
                                    marginTop: '5px',
                                    width: '300px',
                                    maxHeight: '300px',
                                    objectFit: 'contain',
                                }}
                                src={activeOption.imageFile.fileUrl}
                                alt={activeOption.imageFile.filemimetype}
                            />
                        )}

                        <List>
                            {activeOption.options.map((answer, index) => (
                                <ListItem
                                    onClick={() => handleAnswerClick(answer)}
                                    sx={{
                                        p: 0,
                                        py: 1,
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: 1,
                                        cursor: 'pointer',
                                        color: theme === 'dark' ? '#fff' : 'inherit',
                                    }}
                                    key={answer.id}
                                >
                                    <Box
                                        sx={{
                                            minWidth: 24,
                                            minHeight: 24,
                                            borderRadius: '50%',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            color: 'white',
                                            fontSize: 11,
                                            fontWeight: 'bold',
                                            backgroundColor: isAnswerSelected(answer) ? '#58a315' : 'gray',
                                        }}
                                    >
                                        {index + 1}
                                    </Box>
                                    {answer.option_text}
                                </ListItem>
                            ))}
                        </List>
                    </>
                </Box>

                <Box sx={{ display: 'flex', gap: 1 }}>
                    {step > 0 && (
                        <Button onClick={onClickPrev} variant="outlined">
                            {t('quiz.back')}
                        </Button>
                    )}
                    <Button
                        onClick={step === activeQuiz.questions.length - 1 ? onClickResult : onClickNext}
                        disabled={!hasActiveAnswers()}
                        variant="contained"
                        color="primary"
                    >
                        {step === activeQuiz.questions.length - 1 ? t('quiz.finish') : t('quiz.next')}
                    </Button>
                </Box>
            </Box>
            <Dialog
                open={imageDialogOpen}
                onClose={() => setImageDialogOpen(false)}
                // PaperProps={{
                //     sx: {
                //         margin: 0,
                //         maxHeight: '100vh',
                //         maxWidth: '100vw',
                //     },
                // }}
            >
                <DialogContent
                    sx={{
                        p: 0,
                        m: 0,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        minHeight: 'auto',
                        '&:first-of-type': {
                            paddingTop: 0,
                        },
                    }}
                >
                    <Box
                        component="img"
                        sx={{
                            maxWidth: '100%',
                            maxHeight: '90vh',
                            width: 'auto',
                            height: 'auto',
                            objectFit: 'contain',
                            display: 'block',
                        }}
                        src={activeOption.imageFile?.fileUrl}
                        alt={activeOption.imageFile?.filemimetype}
                    />
                </DialogContent>
            </Dialog>
        </>
    );
};
